/**
 * Analytics identity bridge (T50).
 *
 * Renders nothing. Watches the authenticated session's canonical backend user
 * id and calls `identifyUser` once per id, so PostHog stitches the anonymous
 * pre-signup history into the identified profile on auth. Covers EVERY auth
 * entry path in one place — login, register, verify, reset, and a cold reload
 * from a cached token — because they all converge on `/auth/me` returning the
 * `User`.
 *
 * Mounted inside the providers in `main.tsx` (needs `ApiProvider` +
 * `QueryClientProvider`). The `useMe` query is gated on token presence, so it
 * stays network-silent for a logged-out visitor — and it shares TanStack's
 * cache with `AuthGuard`'s `useSession`, so this reader adds no extra request.
 *
 * `identifyUser` no-ops when analytics isn't initialized (key unset), so this
 * component is inert on local dev.
 */

import { useEffect, useRef } from 'react';
import { useMe } from '@shopista/shared/api/hooks';
import { identifyUser } from './analytics';
import type { WebTokenStorage } from './auth';

export interface AnalyticsIdentityProps {
  tokens: WebTokenStorage;
}

export function AnalyticsIdentity({ tokens }: AnalyticsIdentityProps): null {
  // Gate the query on token presence so the logged-out branch fires no request
  // (mirrors useSession). getAccessToken is synchronous.
  const hasToken = tokens.getAccessToken() !== null;
  const me = useMe({ enabled: hasToken });

  // Identify once per distinct user id — guard against re-identifying the same
  // id on every render (StrictMode double-invoke, refetches, re-renders).
  const identifiedId = useRef<string | null>(null);

  useEffect(() => {
    const userId = me.data?.id;
    if (!userId || identifiedId.current === userId) return;
    identifiedId.current = userId;
    // T50 (9d) — attach a person property that deep-links straight to this
    // user's admin panel row, so support can jump from a PostHog person to the
    // account without hand-building a URL. The base is hardcoded to the prod web
    // origin: analytics only runs on prod (key unset elsewhere), and the admin
    // panel lives on the web app at app.shopista.ca. Only the id-derived URL is
    // sent — NO email or name — so no PII lands in the person property.
    identifyUser(userId, {
      admin_url: `https://app.shopista.ca/admin/users?open=${encodeURIComponent(userId)}`,
    });
  }, [me.data?.id]);

  return null;
}
